import { apiDownload, apiFetch } from "./client";
import type {
  AuditEntry,
  BulkTenancyResult,
  Contract,
  ContractCredit,
  ContractStatus,
  DailyRevenuePoint,
  DeviceRow,
  IdleRow,
  RenewalRow,
  SyncConflict,
  SyncRun,
  User,
} from "@/types/api";

export const auth = {
  setupRequired: () => apiFetch<{ setup_required: boolean }>("/auth/setup-required"),
  setup: (body: { username: string; email?: string; password: string }) =>
    apiFetch<User>("/auth/setup", { method: "POST", body: JSON.stringify(body) }),
  login: (username: string, password: string) =>
    apiFetch<{ access_token: string }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  logout: () => apiFetch<{ ok: boolean }>("/auth/logout", { method: "POST" }),
  me: () => apiFetch<User>("/auth/me"),
  changeMyPassword: (current_password: string, new_password: string) =>
    apiFetch<{ ok: boolean; sessions_revoked: number }>("/auth/me/change-password", {
      method: "POST",
      body: JSON.stringify({ current_password, new_password }),
    }),
  updateUser: (
    id: string,
    patch: { email?: string | null; password?: string; role?: User["role"]; is_active?: boolean },
  ) =>
    apiFetch<User>(`/auth/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  listUsers: () => apiFetch<User[]>("/auth/users"),
  createUser: (body: {
    username: string;
    email?: string;
    password: string;
    role: User["role"];
  }) => apiFetch<User>("/auth/users", { method: "POST", body: JSON.stringify(body) }),
};

export const inventory = {
  list: (params: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === "" || v === false) continue;
      qs.set(k, String(v));
    }
    const path = qs.size ? `/inventory?${qs}` : "/inventory";
    return apiFetch<DeviceRow[]>(path);
  },
  get: (id: number) => apiFetch<DeviceRow>(`/inventory/${id}`),
  revenue: (id: number, start: string, end: string) =>
    apiFetch<DailyRevenuePoint[]>(`/inventory/${id}/revenue?start=${start}&end=${end}`),
};

export interface TenantOption {
  id: number;
  name: string;
  slug: string | null;
}

export const tenancy = {
  listTenants: () => apiFetch<TenantOption[]>("/tenancy/tenants"),
  createTenant: (name: string, slug?: string) =>
    apiFetch<TenantOption>("/tenancy/tenants", {
      method: "POST",
      body: JSON.stringify({ name, slug }),
    }),
  update: (deviceId: number, tenant_id: number | null, reason: string) =>
    apiFetch(`/tenancy/${deviceId}`, {
      method: "PATCH",
      body: JSON.stringify({ tenant_id, reason }),
    }),
  bulk: (device_ids: number[], tenant_id: number | null, reason: string) =>
    apiFetch<BulkTenancyResult>("/tenancy/bulk", {
      method: "PATCH",
      body: JSON.stringify({ device_ids, tenant_id, reason }),
    }),
};

export const contracts = {
  list: (params: { status?: ContractStatus; customer?: string; end_within_days?: number } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined) continue;
      qs.set(k, String(v));
    }
    const path = qs.size ? `/contracts?${qs}` : "/contracts";
    return apiFetch<Contract[]>(path);
  },
  get: (id: string) => apiFetch<Contract>(`/contracts/${id}`),
  create: (
    body: Partial<Contract> & { netbox_device_ids: number[]; tempo_deployment_id?: string | null },
  ) =>
    apiFetch<Contract & { deployment_assignment: AssignmentResult | null }>("/contracts", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (id: string, patch: Partial<Contract>) =>
    apiFetch<Contract>(`/contracts/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  cancel: (id: string) => apiFetch<{ ok: boolean }>(`/contracts/${id}`, { method: "DELETE" }),
  deletePermanently: (id: string) =>
    apiFetch<{ ok: boolean }>(`/contracts/${id}/permanent`, { method: "DELETE" }),
  addServers: (id: string, netbox_device_ids: number[]) =>
    apiFetch<Contract>(`/contracts/${id}/servers`, {
      method: "POST",
      body: JSON.stringify({ netbox_device_ids }),
    }),
  removeServer: (id: string, deviceId: number) =>
    apiFetch<Contract>(`/contracts/${id}/servers/${deviceId}`, { method: "DELETE" }),
  listCredits: (id: string) => apiFetch<ContractCredit[]>(`/contracts/${id}/credits`),
  addCredit: (id: string, body: Pick<ContractCredit, "credit_date" | "end_date" | "amount" | "reason" | "notes">) =>
    apiFetch<ContractCredit>(`/contracts/${id}/credits`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteCredit: (id: string, creditId: string) =>
    apiFetch<{ ok: boolean }>(`/contracts/${id}/credits/${creditId}`, { method: "DELETE" }),
};

export const renewals = {
  list: (within_days = 60) => apiFetch<RenewalRow[]>(`/renewals?within_days=${within_days}`),
};

export const idle = {
  list: (within_days = 14) => apiFetch<IdleRow[]>(`/idle?within_days=${within_days}`),
};

export interface DeploymentSummary {
  id: string;
  name: string;
  gpu_type_name: string | null;
  num_nodes: number;
  gpus_per_node: number;
  is_archived: boolean;
  assigned_device_count: number;
}

export interface AssignmentResult {
  succeeded: number[];
  failed: { device_id: number; error: string; detail?: Record<string, unknown> }[];
}

export interface DeploymentRevenueRow {
  tempo_deployment_id: string | null;
  name: string;
  gpu_type_name: string | null;
  financing_type: string | null;
  financing_enabled: boolean;
  contract_enabled: boolean;
  contract_rate_per_gpu_hour: number | null;
  num_nodes: number | null;
  gpus_per_node: number | null;
  device_count: number;
  realized_revenue: string;
  projected_revenue: string | null;
  is_archived: boolean;
  tempo_url: string | null;
}

export interface GpuTypeRevenueRow {
  gpu_type: string | null;
  device_count: number;
  realized_revenue: string;
  revenue_per_device: string;
}

export interface ContractRevenueRow {
  contract_id: string;
  customer_name: string;
  status: string;
  start_date: string;
  end_date: string;
  billing_amount: string;
  billing_frequency: string;
  device_count: number;
  realized_revenue: string;
  expected_billing_for_window: string;
  credits_in_window: string;
}

export interface TenantRevenueRow {
  tenant: string | null;
  device_count: number;
  idle_device_count: number;
  realized_revenue: string;
}

export interface DeploymentDeviceRow {
  netbox_device_id: number;
  name: string;
  tenant: string | null;
  gpu_type: string | null;
  realized_revenue: string;
  last_earning_date: string | null;
}

export interface DeploymentDetail {
  tempo_deployment_id: string;
  name: string;
  gpu_type_name: string | null;
  financing_type: string | null;
  financing_enabled: boolean;
  contract_enabled: boolean;
  contract_rate_per_gpu_hour: number | null;
  num_nodes: number | null;
  gpus_per_node: number | null;
  devices: DeploymentDeviceRow[];
  realized_revenue: string;
  tempo_url: string | null;
  raw: Record<string, unknown>;
}

export type RevenueDimension = "deployment" | "gpu_type" | "contract" | "tenant";
export type RevenueGranularity = "daily" | "weekly" | "monthly";

export interface RevenueSeriesGroup {
  label: string;
  values: string[];
}
export interface RevenueSeriesResponse {
  granularity: RevenueGranularity;
  dimension: RevenueDimension;
  periods: string[];
  groups: RevenueSeriesGroup[];
  total_per_period: string[];
}

export interface ProjectionPoint {
  period: string;
  revenue: string;
}
export interface ProjectionResponse {
  tempo_deployment_id: string;
  granularity: string;
  series: ProjectionPoint[];
  total_in_window: string;
  computed_at: string;
  cached: boolean;
}

const windowQs = (start?: string, end?: string) => {
  const qs = new URLSearchParams();
  if (start) qs.set("start", start);
  if (end) qs.set("end", end);
  return qs.size ? `?${qs}` : "";
};

export const revenue = {
  byDeployment: (start?: string, end?: string) =>
    apiFetch<DeploymentRevenueRow[]>(`/revenue/by-deployment${windowQs(start, end)}`),
  byGpuType: (start?: string, end?: string) =>
    apiFetch<GpuTypeRevenueRow[]>(`/revenue/by-gpu-type${windowQs(start, end)}`),
  byContract: (start?: string, end?: string) =>
    apiFetch<ContractRevenueRow[]>(`/revenue/by-contract${windowQs(start, end)}`),
  byTenant: (start?: string, end?: string) =>
    apiFetch<TenantRevenueRow[]>(`/revenue/by-tenant${windowQs(start, end)}`),
  // Per-server revenue breakdown CSV (one row per server × period bucket, at
  // the given granularity; columns: GPU type, deployment, tenant, contract,
  // realized revenue) for the window — triggers a browser download.
  exportDetailCsv: (start: string, end: string, granularity: RevenueGranularity) => {
    const qs = new URLSearchParams({ start, end, granularity });
    return apiDownload(`/revenue/export?${qs}`, "revenue-export.csv");
  },
  deploymentDetail: (id: string, start?: string, end?: string) =>
    apiFetch<DeploymentDetail>(`/revenue/by-deployment/${id}${windowQs(start, end)}`),
  deploymentProjection: (id: string, start?: string, end?: string, refresh = false) => {
    const qs = new URLSearchParams();
    if (start) qs.set("start", start);
    if (end) qs.set("end", end);
    if (refresh) qs.set("refresh", "true");
    return apiFetch<ProjectionResponse>(
      `/revenue/by-deployment/${id}/projection${qs.size ? `?${qs}` : ""}`,
    );
  },
  series: (
    dimension: RevenueDimension,
    granularity: RevenueGranularity,
    start: string,
    end: string,
    top = 10,
  ) => {
    const qs = new URLSearchParams({ dimension, granularity, start, end, top: String(top) });
    return apiFetch<RevenueSeriesResponse>(`/revenue/series?${qs}`);
  },
  deploymentTimeseries: (
    id: string,
    granularity: RevenueGranularity,
    start: string,
    end: string,
  ) => {
    const qs = new URLSearchParams({ granularity, start, end });
    return apiFetch<DeploymentTimeseries>(
      `/revenue/by-deployment/${id}/timeseries?${qs}`,
    );
  },
};

export interface DeploymentTimeseries {
  tempo_deployment_id: string;
  granularity: RevenueGranularity;
  periods: string[];
  realized: string[];
  projected: string[];
  realized_total: string;
  projected_total: string;
  projection_available: boolean;
  projection_unavailable_reason: string | null;
}

export const deployments = {
  list: () => apiFetch<DeploymentSummary[]>("/deployments"),
  assign: (tempoId: string, netbox_device_ids: number[]) =>
    apiFetch<AssignmentResult>(`/deployments/${tempoId}/devices`, {
      method: "POST",
      body: JSON.stringify({ netbox_device_ids }),
    }),
  unassign: (tempoId: string, deviceId: number) =>
    apiFetch<AssignmentResult>(`/deployments/${tempoId}/devices/${deviceId}`, {
      method: "DELETE",
    }),
  clearAll: (tempoId: string) =>
    apiFetch<AssignmentResult>(`/deployments/${tempoId}/devices`, {
      method: "DELETE",
    }),
};

export const sync = {
  runs: (job_name?: string) =>
    apiFetch<SyncRun[]>(job_name ? `/sync/runs?job_name=${job_name}` : "/sync/runs"),
  conflicts: (runId: string) => apiFetch<SyncConflict[]>(`/sync/runs/${runId}/conflicts`),
  triggerVastMachineId: () => apiFetch<SyncRun>("/sync/vast-machine-ids", { method: "POST" }),
  triggerEarningsRollup: (full = false, days = 7) =>
    apiFetch<SyncRun>(`/sync/vast-earnings-rollup?full=${full}&days=${days}`, { method: "POST" }),
};

export const audit = {
  list: (params: {
    target_kind?: string;
    target_id?: string;
    action?: string;
    limit?: number;
  } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined) continue;
      qs.set(k, String(v));
    }
    const path = qs.size ? `/audit?${qs}` : "/audit";
    return apiFetch<AuditEntry[]>(path);
  },
};
